import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime } from 'rxjs';
import {
  Tutor,
  TutorService,
  TutorSettingItem,
} from '../../../services/tutor.service';
import { OrganisationService } from '../../../services/organisation.service';
import { AgentRefreshService } from '../../../agent/agent-refresh.service';
import { VIRTUAL_BOT_SETTINGS_BY_VAR } from '../../../pages/tutors/tutor-id/ai-settings/ai-settings.sections';

/** How long after an agent write a changed setting value counts as the agent's. */
const AGENT_WRITE_WINDOW_MS = 5000;

@Component({
  selector: 'app-tutor-settings',
  templateUrl: './tutor-settings.component.html',
  styleUrl: './tutor-settings.component.scss',
})
export class TutorSettingsComponent implements OnInit, OnChanges {
  @Input() tutorSetting?: TutorSettingItem;
  @Input() tutor?: Tutor;
  @Input() linkedSetting?: TutorSettingItem;
  @Input() disabled = false;
  @Input() disabledHintKey?: string;
  @Output() settingValueSaved = new EventEmitter<{
    tutorId: string;
    settingId: string;
    value: any;
  }>();

  form!: FormGroup;
  linkedForm!: FormGroup;
  private initialized = false;
  /** When the in-house agent last wrote settings (0 = not pending for this row). */
  private agentWroteAt = 0;

  constructor(
    private fb: FormBuilder,
    protected tutorService: TutorService,
    private translate: TranslateService,
    private organisationService: OrganisationService,
  ) {
    inject(AgentRefreshService)
      .on('bot_settings')
      .pipe(takeUntilDestroyed())
      .subscribe(() => (this.agentWroteAt = Date.now()));
    this.form = this.fb.group({
      setting_id: [1, [Validators.required]],
      name: ['', [Validators.required]],
      field_type: ['text', [Validators.required]],
      default_value: [''],
      value: [''],
    });
    this.linkedForm = this.fb.group({ value: [''] });
  }

  /**
   * Variables tagged "Coming Soon" — rendered with the input disabled
   * and a small badge so admins can see the planned shape but can't
   * configure them until the backend handler lands. Currently used for
   * the `integration_*` ticket-handoff fields on alpha_support; the
   * settings exist in template_settings (migration 030) but writing
   * to them does nothing yet because the dispatcher isn't built.
   */
  private static readonly COMING_SOON_PREFIXES = ['integration_'];

  /**
   * Variables that belong to the **dozent feature** on alpha_tutor /
   * alpha_flexible templates. When the client doesn't have the
   * `DOZENT` feature flag enabled, these settings still render
   * (so admins see what's available in the upgrade) but are disabled
   * and badged "Coming Soon", same UX as the integration_* fields.
   * The set is conservative — only fields that ONLY make sense with
   * a real dozent attached. Knobs that work standalone (e.g. plain
   * persona blocks) stay editable.
   */
  private static readonly DOZENT_FEATURE_VARS = new Set<string>([
    'lecturer_mode',
    'dozent_name',
    'teacher_persona',
    'teacher_introduction',
    'faq_enabled',
    'enable_question_queue',
    'learnings_store_enabled',
  ]);

  private static readonly WIDGET_TAB_HINT_VARS = new Set<string>([
    'tab_exercises_enabled',
    'tab_messages_enabled',
    'tab_notifications_enabled',
  ]);

  /** True when the write target of this row is a real Twin bot
   *  (alpha_dozent template) — either the Twin's own settings page
   *  (`tutor` = the twin) or a linked-twin tile on tutor/flex
   *  (`tile.targetTutor` = the twin, passed through as `tutor`).
   *  Mirrors the `isTwin` lookup in `ai-settings.component.ts`. */
  get isTwinTarget(): boolean {
    const templateId = this.tutor?.template_id;
    if (!templateId) return false;
    const template = this.tutorService.tutorTemplates$.value.find(
      (t) => t.id === templateId
    );
    return !!template && template.name === 'alpha_dozent';
  }

  /** True when the active setting belongs to an unfinished feature
   *  OR a feature the client hasn't unlocked yet. Both cases render
   *  identically (disabled + Coming Soon badge). */
  get isComingSoon(): boolean {
    const v = this.tutorSetting?.variable_name;
    if (!v) return false;
    if (
      TutorSettingsComponent.COMING_SOON_PREFIXES.some((p) => v.startsWith(p))
    ) {
      return true;
    }
    if (TutorSettingsComponent.DOZENT_FEATURE_VARS.has(v)) {
      // Whoever owns a real Twin bot must be able to fill its persona
      // fields (Twin Name / Twin-Persona / Selbstauskunft) — the
      // DOZENT gate is an upsell hint for tutor/flex bots WITHOUT a
      // twin, not a lock on an existing twin's core settings.
      if (this.isTwinTarget) return false;
      return !this.organisationService.hasFeature('DOZENT');
    }
    return false;
  }

  get hintTranslationKey(): string | null {
    const variableName = this.tutorSetting?.variable_name;
    if (
      variableName &&
      TutorSettingsComponent.WIDGET_TAB_HINT_VARS.has(variableName)
    ) {
      return 'enums.aiConfigurationHints.' + variableName;
    }

    const candidates = [
      this.tutorSetting?.name,
      variableName,
    ].filter(Boolean) as string[];
    for (const candidate of candidates) {
      const key = 'enums.aiConfigurationHints.' + candidate;
      const resolved = this.translate.instant(key);
      if (resolved && resolved !== key) return key;
    }
    return null;
  }

  /** Hint icon appears whenever a translation exists for this setting. */
  get hasHint(): boolean {
    return !!this.hintTranslationKey;
  }

  /** The row's single popover: why it is locked, else its regular hint. */
  get popoverKey(): string | null {
    return this.disabled && this.disabledHintKey
      ? this.disabledHintKey
      : this.hintTranslationKey;
  }

  /** The agent's optional default language (migration 198). */
  get isLanguageSetting(): boolean {
    return this.tutorSetting?.variable_name === 'default_language';
  }

  /** True for field types that need full-width rendering (label above input). */
  get isFullWidth(): boolean {
    if (this.isLanguageSetting) return false;
    return (
      this.tutorSetting?.field_type === 'longtext' ||
      this.tutorSetting?.field_type === 'code' ||
      this.tutorSetting?.field_type === 'text'
    );
  }

  getThrottleTime() {
    // A pick, not typing: only '' or a listed code ever reaches the form.
    if (this.isLanguageSetting) {
      return 0;
    }

    if (this.tutorSetting?.field_type === 'boolean') {
      return 0;
    }

    if (this.tutorSetting?.field_type === 'dropdown') {
      return 0;
    }

    if (this.tutorSetting?.field_type === 'multi-select') {
      return 0;
    }

    return 500;
  }

  get toggleOn(): boolean {
    return !!this.form.get('value')?.value;
  }

  /** See `tutor-wizard.component.resolveDefault` — same i18n-key
   *  resolution for `template_settings.default_value`. Used here so a
   *  freshly-instantiated bot (no per-bot value yet) shows the
   *  locale-appropriate default instead of a raw `templateDefaults.…`
   *  key string.
   *
   *  Sync helper for legacy callers — note that this returns the raw
   *  key when the HTTP-loaded i18n bundle hasn't arrived yet. Prefer
   *  `applyResolvedDefault()` below in async paths so the form gets
   *  re-patched once translations land. */
  private isTemplateDefaultKey(raw: unknown): raw is string {
    return typeof raw === 'string' && raw.startsWith('templateDefaults.');
  }

  private resolveDefault(raw: unknown): string {
    if (typeof raw !== 'string' || !raw) return (raw as any) ?? '';
    if (!this.isTemplateDefaultKey(raw)) return raw;
    const translated = this.translate.instant(raw);
    return translated && translated !== raw ? translated : raw;
  }

  /** Async-aware resolver for `templateDefaults.*` keys. Subscribes to
   *  `translate.get()` (which waits for the HTTP-loaded i18n bundle)
   *  and re-patches the matching form control once the translation
   *  arrives — fixes the case where `ngOnInit` runs before the i18n
   *  loader has finished and `translate.instant` would otherwise
   *  return the raw key string. */
  private applyResolvedDefault(
    raw: unknown,
    onResolved: (text: string) => void,
  ): void {
    if (!this.isTemplateDefaultKey(raw)) return;
    this.translate.get(raw).subscribe((translated: string) => {
      if (translated && translated !== raw) onResolved(translated);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.initialized) return;
    const agentWrite = changes['tutorSetting'] || changes['linkedSetting'] ? this.takeAgentWrite() : false;
    if (changes['tutorSetting']) {
      // Race fix: only re-patch the form when we're actually showing a
      // DIFFERENT setting (setting_id changed). Same setting_id with a
      // new value is our own PATCH echoing back through the service's
      // BehaviorSubject — re-patching would clobber characters the
      // user typed during the in-flight save (observed as "swallowed
      // letters / spaces" in textareas).
      // Exception: right after the in-house agent changed settings, a new value
      // is the agent's, not our echo — show it, or the field keeps the old
      // value while the agent reports it changed (once per row, then the
      // guard holds again).
      const prev = changes['tutorSetting'].previousValue;
      const curr = changes['tutorSetting'].currentValue;
      if (prev?.setting_id !== curr?.setting_id || (agentWrite && prev?.value !== curr?.value)) {
        this.patchTutorSettingForm();
        this.applyDisabledState();
      }
    }
    if (changes['linkedSetting']) {
      // Same guard for the linked setting (paired bot-backed value).
      const prev = changes['linkedSetting'].previousValue;
      const curr = changes['linkedSetting'].currentValue;
      if (prev?.setting_id !== curr?.setting_id || (agentWrite && prev?.value !== curr?.value)) {
        this.patchLinkedSettingForm();
      }
    }
    if (changes['disabled']) {
      this.applyDisabledState();
    }
  }

  /** True once — for the first input change shortly after an agent write. */
  private takeAgentWrite(): boolean {
    const pending = this.agentWroteAt > 0 && Date.now() - this.agentWroteAt < AGENT_WRITE_WINDOW_MS;
    this.agentWroteAt = 0;
    return pending;
  }

  private patchTutorSettingForm() {
    const patch: any = { ...this.tutorSetting };

    // NbSelect multiple requires an array value
    if (patch.field_type === 'multi-select') {
      let val = patch.value ?? patch.default_value;
      if (typeof val === 'string') {
        try { val = JSON.parse(val); } catch { val = []; }
      }
      patch.value = Array.isArray(val) ? val : val != null ? [val] : [];
    }

    if (patch.field_type === 'dropdown' && patch.value == null) {
      patch.value = patch.default_value;
    }

    // Numeric fields and toggles: a bot without a per-bot row must show the
    // template default (20, 0.3, true, …) instead of an empty input / stuck
    // slider / off-toggle. Matches the backend, which resolves missing
    // instance rows to the template default at session init.
    if (
      (patch.field_type === 'int' ||
        patch.field_type === 'slider' ||
        patch.field_type === 'boolean') &&
      patch.value == null
    ) {
      patch.value = patch.default_value;
    }

    // Dummy-text policy: show the localized template default ONLY
    // while the per-bot value is still the un-resolved
    // `templateDefaults.…` key as seeded by `assign_template_settings`
    // at bot creation. Once the user has typed (or explicitly
    // cleared) the field, `value` is an authoritative string and
    // must NOT be re-overwritten by the default — otherwise the
    // dummy comes back every time the user clears it, which defeats
    // its purpose as a one-time orientation hint.
    const rawValue = patch.value;
    const rawDefault = patch.default_value;
    if (this.isTemplateDefaultKey(rawValue)) {
      patch.value = this.resolveDefault(rawValue);
    }
    if (typeof rawDefault === 'string') {
      patch.default_value = this.resolveDefault(rawDefault);
    }

    this.form.patchValue(patch, { emitEvent: false });

    // Async re-patch: when `translate.instant` returned the raw key
    // because the HTTP i18n bundle was still in flight, wait for it
    // and overwrite once the bundle arrives — `value` only when it's
    // still the raw key (i.e., the user hasn't typed yet),
    // `default_value` always.
    if (this.isTemplateDefaultKey(rawValue)) {
      this.applyResolvedDefault(rawValue, (translated) => {
        const cur = this.form.get('value')?.value;
        if (cur === rawValue) {
          this.form.get('value')?.setValue(translated, { emitEvent: false });
        }
      });
    }
    if (this.isTemplateDefaultKey(rawDefault)) {
      this.applyResolvedDefault(rawDefault, (translated) => {
        this.form.get('default_value')?.setValue(translated, { emitEvent: false });
      });
    }
  }

  private patchLinkedSettingForm() {
    if (!this.linkedSetting || !this.linkedForm) return;
    let val = this.linkedSetting.value ?? this.linkedSetting.default_value;
    if (typeof val === 'string') {
      try { val = JSON.parse(val); } catch { val = []; }
    }
    this.linkedForm.patchValue(
      { value: Array.isArray(val) ? val : val != null ? [val] : [] },
      { emitEvent: false }
    );
  }

  ngOnInit() {
    this.patchTutorSettingForm();
    this.applyDisabledState();

    this.form.valueChanges
      .pipe(debounceTime(this.getThrottleTime()))
      .subscribe((value) => {
        if (this.disabled || this.isComingSoon) return;
        // Stale-item guard: after switching tutors, the global settings
        // cache can briefly hold the previous bot's rows. Never PATCH a
        // setting that belongs to a different bot than the page's tutor.
        if (
          this.tutorSetting?.bot_id &&
          this.tutor?.id &&
          String(this.tutorSetting.bot_id) !== String(this.tutor.id)
        ) {
          return;
        }
        // Virtual bot-backed setting → write to the bots table directly,
        // not to the template_settings PATCH endpoint.
        const virtual =
          this.tutorSetting?.variable_name &&
          VIRTUAL_BOT_SETTINGS_BY_VAR[this.tutorSetting.variable_name];
        if (virtual) {
          this.tutorService
            .updateTutor({
              id: this.tutor!.id,
              [virtual.bot_field]: value.value,
            } as Partial<Tutor>)
            .subscribe();
          return;
        }

        this.tutorService
          .updateTutorSettings(this.tutor!.id, {
            [this.tutorSetting!.setting_id + '']: value.value,
          })
          .subscribe(() => {
            this.settingValueSaved.emit({
              tutorId: this.tutor!.id,
              settingId: String(this.tutorSetting!.setting_id),
              value: value.value,
            });
          });
      });

    this.patchLinkedSettingForm();
    this.linkedForm.valueChanges
      .pipe(debounceTime(0))
      .subscribe((v) => {
        if (this.disabled || this.isComingSoon) return;
        if (!this.linkedSetting) return;
        this.tutorService
          .updateTutorSettings(this.tutor!.id, {
            [this.linkedSetting.setting_id + '']: v.value,
          })
          .subscribe(() => {
            this.settingValueSaved.emit({
              tutorId: this.tutor!.id,
              settingId: String(this.linkedSetting!.setting_id),
              value: v.value,
            });
          });
      });
    this.initialized = true;
  }

  private applyDisabledState() {
    // Coming-Soon / explicit read-only variables: disable the entire
    // form so the value displays read-only. Disabled controls don't emit
    // valueChanges, so the PATCH below stays inert by construction.
    if (this.isComingSoon || this.disabled) {
      this.form.disable({ emitEvent: false });
      this.linkedForm.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
      this.linkedForm.enable({ emitEvent: false });
    }
  }
}
